// Angular
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { formatNumber } from '@angular/common';
// Google Map
import { GoogleMap, MapInfoWindow } from '@angular/google-maps';
// Model
import { MapViewConfigModel } from './models/map-view.config.model';
// RXJS
import { Observable } from 'rxjs';
// Service
import { MapService } from './services/map.service';

export enum MEASURE_DISTANCE_LABEL {
  MEASURE_DISTANCE = 'Measure distance',
  CLEAR_MEASUREMENT = 'Clear measurement',
}
export enum UNITS {
  MILES = 'miles',
  FEET = 'ft',
}

@Component({
  selector: 'cv-component-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, OnChanges {
  defaultIcon = 'assets/img/marker/marker-map-location.png';
  defaultMarkersOptions = { draggable: true, optimized: false };
  defaultWidth = '100%';

  @Input() mapConfiguration: MapViewConfigModel;
  @Input() width: string;
  @Input() height: string;
  @Input() zoom: number;
  @Input() fitBounds: boolean;
  @Input() isPinSelectorMode: boolean;
  @Input() isMapDisabled: boolean;
  @Output() newMarkerEmmiter: EventEmitter<{ lat: any, lng: any }> = new EventEmitter<{ lat: any, lng: any }>();

  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  @ViewChild('googleMap', { static: false }) mapComponent: GoogleMap;

  public randomIdMap = Math.floor(Math.random() * 10000).toString();
  public googleAPISet: Observable<boolean>;
  public newMarker: { lat: any, lng: any } = null;
  public loaded = false;
  public showLabelsFlag = false;
  public poly;
  public polyShadow;
  public isPolylineEnabled: boolean;
  public addNewPath: boolean;
  public measureDistanceLabel: string;
  public currentUnit: string;
  // To Create Marker on Fly
  constructor(
    private mapService: MapService,
  ) {
  }

  ngOnInit(): void {
	if (this.mapConfiguration.mapKey) {
		this.mapService.setMapKey(this.mapConfiguration.mapKey);
	}

    this.googleAPISet = this.mapService.isGoogleMapAPISet();
    this.setBounds();
    this.setCheckLabels();
    this.isPolylineEnabled = false;
    this.addNewPath = false;
    this.measureDistanceLabel = MEASURE_DISTANCE_LABEL.MEASURE_DISTANCE;
  }

  // checks whether is a selected rule has changed
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isPinSelectorMode) {
      this.onChangePinSelectorMode(changes.isPinSelectorMode);
    }
  }

  onChangePinSelectorMode(isPinMode) {
    if (!this.isPinSelectorMode) {
      this.newMarker = null;
      this.newMarkerEmmiter.emit(this.newMarker);
    } else {
      const length = this.poly.getPath().length;
      this.clearPathAndInfoWindow(length);
      this.isPolylineEnabled = false;
    }
    this.measureDistanceLabel = MEASURE_DISTANCE_LABEL.MEASURE_DISTANCE;
    this.currentUnit = UNITS.MILES;
  }

  onIdle() {
    if (!this.loaded) {
      this.currentUnit = UNITS.MILES;
      this.loaded = true;
      this.setBounds();
      this.setCheckLabels()
      this.setPolyline();
      this.setContextMenu();
      this.setUnitConvertButton();

      //event listener for remove clicked vertex (path)
      google.maps.event.addListener(this.poly, 'click', (res) => {
        if (res.vertex === undefined) {
          return;
        }

        const path = this.poly.getPath();
        const length = path.length;
        const shawdowPath = this.polyShadow.getPath();

        //if the length is equal to 2, then remove all polyline from the map
        //otherwise, remove the selected(clicked) vertex from the polyline
        if (length === 2) {
          for (let i = 0; i < length; i++) {
            path.removeAt(0);
            shawdowPath.removeAt(0);
          }
        } else if (length > 2 && res.vertex !== 0) {
          path.removeAt(res.vertex);
          shawdowPath.removeAt(res.vertex);
        }

        //update the infoWindow for total path distance
        this.setPathDistanceInfoWindow(true);
      })

      //event listener for click and drag the existing vertex
      google.maps.event.addListener(this.poly.getPath(), 'set_at', (res) => {
        const path = this.poly.getPath();
        const shadowPath = this.polyShadow.getPath();
        shadowPath.setAt(res, path.getAt(res));
        this.setPathDistanceInfoWindow(true);
      });

      //event listener for add new vertex and its edge by click and drag from a circle in between the existing vertexs
      //this event listener is also triggered after the new LatLng is pushed to the the path array. so added a flag to
      //only to work for the behavior in the first line in this comment.
      google.maps.event.addListener(this.poly.getPath(), 'insert_at', (res) => {
        if (!this.addNewPath) {
          const path = this.poly.getPath();
          const shadowPath = this.polyShadow.getPath();
          shadowPath.insertAt(res, path.getAt(res));
          this.setPathDistanceInfoWindow(false);
        }
      });

      // event listener for "contextmenu" or "right-click" event on google maps
      google.maps.event.addListener(this.mapComponent.googleMap, 'contextmenu', (event) => {
        this.showContextMenu(event);
      });
      // event listener for "click" event on google maps
      google.maps.event.addListener(this.mapComponent.googleMap, 'click', (event) => {
        // if 'contextmenu' is opened then hide it
        this.hideContextMenu();
      });
      // event listener for "drag" on google maps
      google.maps.event.addListener(this.mapComponent.googleMap, 'drag', (event) => {
        // if 'contextmenu' is opened then hide it
        setTimeout(() => {
          this.hideContextMenu();
        }, 50);

      });
      // event listener for "zoom_changed" event on google maps
      google.maps.event.addListener(this.mapComponent.googleMap, 'zoom_changed', (event) => {
        // if 'contextmenu' is opened then hide it
        this.hideContextMenu();
      });
      // event listener for "tilt_changed" event on google maps
      google.maps.event.addListener(this.mapComponent.googleMap, 'tilt_changed', (event) => {
        // if 'contextmenu' is opened then hide it
        this.hideContextMenu();
      });
      // event listener for "projection_changed" event on google maps
      google.maps.event.addListener(this.mapComponent.googleMap, 'projection_changed', (event) => {
        // if 'contextmenu' is opened then hide it
        this.hideContextMenu();
      });

    }
  }

  /**
   * hide context menu
   */
  hideContextMenu() {
    const contextMenu = document.getElementById(
      'contextMenu' + this.randomIdMap
    ) as HTMLElement;

    contextMenu.style.display = 'none';
  }

  /**
   * show context menu
   * @param event
   */
  showContextMenu(event: any) {
    let clickedPositionX = event.pixel.x;
    let clickedPositionY = event.pixel.y;

    const contextMenu = document.getElementById(
      'contextMenu' + this.randomIdMap
    ) as HTMLElement;
    contextMenu.style.display = 'block';

    // prevent to show the windows context menu from the google maps
    const googleMapContainer = document.getElementById('google-map-container');
    google.maps.event.addDomListener(contextMenu, 'contextmenu', function (e) {
      e.preventDefault();
      e.stopPropagation();
    });

    // get the google maps container width and height
    const googleMapWidth = googleMapContainer.offsetWidth;
    const googleMapHeight = googleMapContainer.offsetHeight;

    // get the context menus width and height
    const contextMenuWidth = contextMenu.offsetWidth;
    const contextMenuHeight = contextMenu.offsetHeight;

    // calculate the x position for context menu
    if ((googleMapWidth - event.pixel.x) < contextMenuWidth) {
      clickedPositionX = clickedPositionX - contextMenuWidth
    }

    // calculate the y position for context menu
    if ((googleMapHeight - event.pixel.y) < contextMenuHeight) {
      clickedPositionY = clickedPositionY - contextMenuHeight
    }

    contextMenu.style.left = clickedPositionX + 'px';
    contextMenu.style.top = clickedPositionY + 'px';
  }

  /**
   * set contextmenu
   */
  setContextMenu() {
    if (this.mapComponent) {
      const contextMenu = document.getElementById(
        'contextMenu' + this.randomIdMap
      ) as HTMLElement;
      this.mapComponent.controls[google.maps.ControlPosition.TOP_RIGHT].push(contextMenu);
    }

    this.hideContextMenu();
  }

  setUnitConvertButton() {
    // add custom control button to convert measure unit between miles and ft
    if (this.mapComponent) {
      const styleControl = document.getElementById(
        'convertUnit' + this.randomIdMap
      ) as HTMLElement;
      this.mapComponent.googleMap.controls[google.maps.ControlPosition.BOTTOM_LEFT].push(styleControl);
    }
  }

  /**
   * Toggle to activate or deactivate measure distance functionality on the google map
   * If the deactivate measure distance is clicked then all existing polylines will be removed from the google map
   */
  togglePoly() {
    if (!this.isPinSelectorMode) {
      const length = this.poly.getPath().length;

      if (this.isPolylineEnabled) {
        this.measureDistanceLabel = MEASURE_DISTANCE_LABEL.MEASURE_DISTANCE;
        this.currentUnit = UNITS.MILES;
      } else {
        this.measureDistanceLabel = MEASURE_DISTANCE_LABEL.CLEAR_MEASUREMENT;
      }
      if (this.isPolylineEnabled && length > 0) {
        this.clearPathAndInfoWindow(length);
      }

      this.isPolylineEnabled = !this.isPolylineEnabled;
      this.hideContextMenu();
    }
  }

  setBounds() {
    if (this.fitBounds && this.mapComponent) {
      this.mapComponent.fitBounds(new google.maps.LatLngBounds(this.mapConfiguration.centerLocation, this.mapConfiguration.locationModels[0].position))
    }
  }

  /**
   * Push element into the google map component
   */
  setCheckLabels() {
    if (this.mapComponent) {
      const styleControl = document.getElementById(
        this.randomIdMap
      ) as HTMLElement;
      this.mapComponent.controls[google.maps.ControlPosition.TOP_LEFT].push(styleControl);
    }
  }

  /**
   * set polyline and another polyline to apply shadow on polyline
   */
  setPolyline() {
    // an original polyline with green circle icon for start vertex and red circle for last vertex.
    this.poly = new google.maps.Polyline({
      strokeColor: '#000000',
      strokeOpacity: 1.0,
      strokeWeight: 3,
      editable: true,
      icons: [
        {
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            strokeColor: '#393',
            strokeOpacity: 1
          },
          offset: '0%',
        },
        {
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            strokeColor: '#FF0000',
            strokeOpacity: 1
          },
          offset: '100%',
        },
      ]
    });

    // hide undo icon from polyline
    this.poly.setOptions({
      suppressUndo: true
    })

    this.poly.setMap(this.mapComponent.googleMap);

    // another polyline to apply white shadow on the orignal black polyline
    this.polyShadow = new google.maps.Polyline({
      strokeColor: '#FFFFFF',
      strokeOpacity: 0.3,
      strokeWeight: 10
    });

    this.polyShadow.setMap(this.mapComponent.googleMap);
  }

  /**
   * Where in the Map was the click, attach a Pin on the position if the Clickable Pin is Enabled
   * If the 'Activate Measure Distance' is clicked, then polyline can be drawn on the map to measure distance
   * @param event
   */
  public onClickMap(event: google.maps.PolyMouseEvent): void {
    // Being to validate the clicks on polyline event.
    if (event.latLng !== null && this.isPolylineEnabled) {
      this.addNewPath = true;
      const path = this.poly.getPath();
      path.push(event.latLng as google.maps.LatLng);

      const shadowPath = this.polyShadow.getPath();
      shadowPath.push(event.latLng as google.maps.LatLng);

      this.setPathDistanceInfoWindow(true);

      this.infoWindow.infoWindow.setOptions({
        pixelOffset: new google.maps.Size(0, -20)
      });

      this.infoWindow.infoWindow.open(this.mapComponent.googleMap);
      this.addNewPath = false;
    }

    // Being to validate the Clicks on Pin Mode
    if (this.isPinSelectorMode) {
      this.newMarker = event.latLng.toJSON();
      this.newMarkerEmmiter.emit(this.newMarker);
    }
  }

  /**
   * convert meters to miles
   * @param path
   * @returns
   */
  public convertMetersToMiles(path: google.maps.LatLng[]) {
    const distance = (google.maps.geometry.spherical.computeLength(path) / 1609.34);
    return formatNumber(Number(distance), 'en-US', '1.0-2').toString();
  }
  /**
   * convert meters to feet
   * @param path
   * @returns
   */
  public convertMetersToFeet(path: google.maps.LatLng[]) {
    const distance = (google.maps.geometry.spherical.computeLength(path)  * 3.28084);
    return formatNumber(Number(distance), 'en-US', '1.0-2').toString();
  }

  /**
   * Update total length of all paths and set infoWindow at last vertex of the path.
   * @param setPosition
   */
  public setPathDistanceInfoWindow(setPosition: boolean) {
    const path = this.poly.getPath();
    const content = this.setInfoWindowContent(path);
    this.infoWindow.infoWindow.setContent(content);
    if (setPosition) {
      this.infoWindow.infoWindow.setPosition(path.getAt(path.length - 1));
    }
  }

  /**
   * returns distance content
   * @param path
   * @returns
   */
  public setInfoWindowContent(path: google.maps.LatLng[]){
    if(google.maps.geometry.spherical.computeLength(path) === 0){
      return '0';
    }
    if(this.currentUnit === UNITS.MILES){
      return this.convertMetersToMiles(path) + ' miles';
    }else{
      return this.convertMetersToFeet(path) + ' ft';
    }
  }

  /**
   * convert unit of measurement
   */
  public convertUnit(currentUnit: string): void {
    if(currentUnit === UNITS.MILES){
      this.currentUnit = UNITS.MILES;
    }else{
      this.currentUnit = UNITS.FEET;
    }

    this.setPathDistanceInfoWindow(false);
  }

  /**
   * clear all path and infoWindow from map
   */
  public clearPathAndInfoWindow(length: number) {
    const path = this.poly.getPath();
    const shadowPath = this.polyShadow.getPath();
    for (let i = 0; i < length; i++) {
      path.removeAt(0);
      shadowPath.removeAt(0);
    }

    this.infoWindow.infoWindow.close();
  }
  /**
   * When making an element draggable, the Google API does not change the marker position
   * this update the object
   * @param event
   */
  public onMarkerDragend(event: any): void {
    this.newMarker = event.latLng;
    this.newMarkerEmmiter.emit(this.newMarker);
  }
  /**
   * Enable or Disable the map
   * @param mapDisabled
   */
  public disableMap(mapDisabled: boolean): void {
    this.isMapDisabled = mapDisabled;
  }

  /**
   * show or hide labels in the map component
   */
  showLabelsToggle() {
    this.showLabelsFlag = !this.showLabelsFlag;
    this.showLabelsFlag ?
      this.mapComponent.googleMap.setMapTypeId('hybrid') :
      this.mapComponent.googleMap.setMapTypeId('satellite')
  }
}
